// Ticketing / vendor / contract / asset / site / team / analytics API client.
import { api } from './api';

export interface Ticket {
  id: string; ticketNumber: string; title: string; status: string; priority: string;
  siteId?: string | null; siteName?: string | null; systemTypeId?: string | null;
  vendorId?: string | null; vendorName?: string | null; slaDeadline?: string | null;
  source: string; createdAt: string;
}
export interface Vendor {
  id: string; name: string; status: string; partnerLevel?: string; notificationEmail?: string;
  phone?: string; categories?: string[]; rating?: number; activeContracts?: number;
  avgResolutionHours?: number; slaCompliance?: number; certifications?: string[];
}
export interface Contract {
  id: string; contractNumber: string; name: string; status: string; type?: string;
  vendorId: string; vendorName?: string; startDate?: string; endDate?: string | null;
  totalValue?: number; currency?: string; sites?: number; systemTypes?: string[];
  slaResponseH?: number; slaResolutionH?: number;
}
export interface SlaPriorityLevel { priority: string; responseTime: number; resolutionTime: number }
export interface EscalationLevel { level: number; role: string; triggerAfter: string; notify: string }
// The raw contract row (detail GET) — incl. the SLA + escalation config jsonb.
export interface ContractDetail {
  id: string; contractNumber: string; name: string; description?: string | null;
  type?: string | null; priority?: string | null; status: string; vendorId?: string | null;
  pricingType?: string | null;                 // monthly|quarterly|yearly|hourly|per_incident|hybrid (payment cadence)
  financials?: { value?: number; currency?: string; [k: string]: unknown } | null;
  globalSla?: { priorityLevels?: SlaPriorityLevel[] } | null;
  escalationPolicy?: { levels?: EscalationLevel[] } | null;
  startDate?: string | null; endDate?: string | null; signedDate?: string | null;
  effectiveDate?: string | null; renewalDate?: string | null;
  renewalNoticeDays?: number | null; autoRenew?: boolean | null;
  notes?: string | null;
  updatedAt?: string;   // optimistic-lock token (WF-01): echo back as If-Match
}
// A per-contract coverage row (which tree node + system type this contract covers).
export interface ContractCoverage {
  id: string; contractId: string;
  nodeType: string; nodeId: string;
  systemTypeId?: string | null; includeSubSystems?: boolean | null;
  coverageLevel?: string | null; isActive?: boolean | null;
}
export interface Asset {
  id: string; assetId: string; name: string; category: string; manufacturer?: string;
  model?: string; serialNumber?: string; siteId?: string; siteName?: string;
  deviceId?: string; systemTypeId?: string; status: string; condition?: string;
  purchasePrice?: number; warrantyEnd?: string | null;
  // §26 lifecycle (demo): procurement-to-retirement state + vendor/contract linkage.
  lifecycle?: string; vendor?: string; contract?: string; ticketHistory?: number;
}
export interface Site {
  id: string; name: string; code: string; city: string; country: string; devices: number;
  status: string; score: number; contacts: string;
}
export interface TeamMember {
  id: string; name: string; email: string; role: string; sites: number; status: string; lastActive: string;
}
export interface SystemType {
  id: string; name: string; code: string; category: string; faultTypes: string[]; deviceCount: number;
}
export interface ResolvedRouting {
  vendorId: string; vendorName?: string | null; contractId: string | null; slaSource: string | null;
  slaDeadline: string | null; source: string;
}
export interface TicketRow {
  id: string; ticketNumber?: string; title: string; status: string; priority: string;
  siteId?: string | null; deviceId?: string | null; systemTypeId?: string | null;
  vendorId?: string | null; contractId?: string | null; slaDeadline?: string | null;
}
export interface Analytics {
  ticketsByStatus: Array<{ name: string; value: number }>;
  ticketsByPriority: Array<{ name: string; value: number }>;
  resolutionTrend: Array<{ day: string; opened: number; resolved: number; avgHours: number }>;
  vendorPerformance: Array<{ vendor: string; resolved: number; avgHours: number; slaCompliance: number }>;
}

// ── Phase-1 service-relationship / dispatch types ──────────────────────────
export interface VendorRelationship {
  id: string; vendorId: string; status: string; vendorAccessStatus: string;
  relationshipStartDate?: string | null; relationshipEndDate?: string | null;
  customerVendorReference?: string | null; notes?: string | null;
}
export interface CoverageRule {
  id: string; contractId: string; ruleType: string; operator: string;
  targetId?: string | null; targetValue?: string | null; priority: number; isActive: boolean;
}
export interface ContractServiceTeamRow {
  id: string; contractId: string; vendorId: string; name: string; serviceMode: string;
  priority: number; isActive: boolean;
}
export interface TechnicianEligibility {
  id: string; contractId: string; technicianId: string; eligibilityType: string;
  approvedByCustomer: boolean; assignmentPriority?: number | null; isActive: boolean;
  /** Technician display name (joined server-side; may be null if not resolvable). */
  technicianName?: string | null;
}
export interface ContractResolutionRow {
  id: string; ticketId: string; selectedContractId?: string | null;
  candidateContractIds: string[]; resolutionStatus: string; resolutionReasons: string[];
  resolvedBySystem: boolean; createdAt: string;
}
export interface AssignmentHistoryRow {
  id: string; ticketId: string; action: string; reason?: string | null;
  technicianId?: string | null; performedBySystem: boolean; createdAt: string;
}

const list = <T>(path: string) => async () => (await api.get<{ data: T[] }>(path)).data.data;

export const ticketingApi = {
  listTickets: async (params?: Record<string, string>) =>
    (await api.get<{ data: Ticket[] }>('/api/v1/tickets', { params })).data.data,
  listVendors: list<Vendor>('/api/v1/vendors'),
  listContracts: list<Contract>('/api/v1/contracts'),
  listAssets: list<Asset>('/api/v1/assets'),
  listSites: list<Site>('/api/v1/sites'),
  listTeam: list<TeamMember>('/api/v1/team'),
  listSystemTypes: list<SystemType>('/api/v1/system-types'),
  analytics: async () => (await api.get<{ data: Analytics }>('/api/v1/analytics')).data.data,

  // ── Writes (W1c) ──────────────────────────────────────────────────────
  createVendor: async (input: { name: string; status?: string; notificationEmail?: string; partnerLevel?: string }) =>
    (await api.post('/api/v1/vendors', input)).data,
  getContract: async (id: string) => (await api.get<{ data: ContractDetail }>(`/api/v1/contracts/${id}`)).data.data,
  // Optimistic-locked edit (WF-01): pass `ifMatch` = the updatedAt the caller
  // last read; the server 409s if the row changed since. Omit for last-write-wins.
  updateContract: async (id: string, patch: Record<string, unknown>, ifMatch?: string) =>
    (await api.patch<{ data: ContractDetail }>(`/api/v1/contracts/${id}`, patch,
      ifMatch ? { headers: { 'If-Match': ifMatch } } : undefined)).data.data,
  createContract: async (input: { contractNumber: string; name: string; vendorId: string; type?: string; status?: string }) =>
    (await api.post('/api/v1/contracts', input)).data,
  // Contract coverage (the per-contract covered nodes that drive routing).
  contractCoverage: async (id: string) =>
    (await api.get<{ data: ContractCoverage[] }>(`/api/v1/contracts/${id}/coverage`)).data.data,
  addCoverage: async (id: string, input: { nodeType: string; nodeId: string; systemTypeId?: string | null; includeSubSystems?: boolean }) =>
    (await api.post<{ data: ContractCoverage }>(`/api/v1/contracts/${id}/coverage`, input)).data.data,
  removeCoverage: async (id: string, coverageId: string) =>
    (await api.delete(`/api/v1/contracts/${id}/coverage/${coverageId}`)).data,
  getAsset: async (id: string) => (await api.get<{ data: Asset }>(`/api/v1/assets/${id}`)).data.data,
  createAsset: async (input: {
    name: string; category: string; systemTypeId?: string; siteId?: string; deviceId?: string;
    manufacturer?: string; model?: string; serialNumber?: string;
  }) => (await api.post('/api/v1/assets', input)).data,
  assetCoverage: async (assetId: string) =>
    (await api.get<{ data: { covered: boolean; contractId?: string; vendorId?: string; reason?: string } }>(`/api/v1/assets/${assetId}/coverage`)).data.data,
  createSystemType: async (input: { name: string; code: string; category?: string }) =>
    (await api.post('/api/v1/system-types', input)).data,
  inviteTeamMember: async (input: { email: string; name: string; role?: string; phone?: string }) =>
    (await api.post<{ data: { invited: boolean; email: string; emailSent?: boolean } }>('/api/v1/team', input)).data.data,
  setTeamRole: async (id: string, role: string) => (await api.patch(`/api/v1/team/${id}`, { role })).data,
  removeTeamMember: async (id: string) => (await api.delete(`/api/v1/team/${id}`)).data,
  createTicket: async (input: {
    title: string; description?: string; priority?: string; severity?: string;
    siteId?: string; buildingId?: string; floorId?: string; zoneId?: string;
    deviceId?: string; assetId?: string; systemTypeId?: string;
    // The health incident this ticket is raised for (real ticket↔incident link).
    incidentId?: string;
  }) => (await api.post<{ data: TicketRow }>('/api/v1/tickets', input)).data.data,
  // Routing preview — resolve vendor + contract + SLA for a location + system type.
  resolveVendor: async (input: {
    priority?: string; systemTypeId?: string; siteId?: string; buildingId?: string;
    floorId?: string; zoneId?: string; deviceId?: string;
  }) => (await api.post<{ data: ResolvedRouting | null }>('/api/v1/tickets/resolve-vendor', input)).data.data,
  listActivityTypes: async () =>
    (await api.get<{ data: Array<{ id: string; name: string; code: string; sortOrder: number; isActive: boolean }> }>('/api/v1/activity-types')).data.data,
  addTicketActivity: async (ticketId: string, input: {
    text: string;
    activityTypeCode?: string;
    activityAt?: string;         // ISO datetime
    durationMins?: number;
    attachments?: Array<{ key: string; name: string; sizeKb?: number; contentType?: string }>;
    newStatus?: string;          // "" / omitted = no change
    internal?: boolean;
  }) => (await api.post(`/api/v1/service-tickets/${ticketId}/activities`, input)).data,
  uploadTicketAttachment: async (ticketId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post<{ data: { key: string; name: string; sizeKb: number; contentType: string } }>(
      `/api/v1/service-tickets/${ticketId}/attachments`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )).data.data;
  },
  attachmentUrl: async (ticketId: string, key: string) =>
    (await api.get<{ data: { url: string } }>(`/api/v1/service-tickets/${ticketId}/attachments/url`, { params: { key } })).data.data.url,
  // Governed lifecycle update (C3): change status/priority/assignee/resolution.
  // The server validates the status transition and records the change; a rejected
  // transition returns HTTP 409.
  updateTicket: async (ticketRowId: string, input: {
    status?: string; priority?: string; assignedTo?: string | null; resolutionNote?: string; comment?: string;
  }, ifMatch?: string) => (await api.patch<{ data: TicketRow }>(`/api/v1/tickets/${ticketRowId}`, input,
    ifMatch ? { headers: { 'If-Match': ifMatch } } : undefined)).data.data,
  // Link this vendor to the cross-tenant Vendor Portal (find-or-create a global
  // vendor account by email). Returns a one-time temp password when newly created.
  inviteVendorToPortal: async (vendorId: string, input: { email: string; name?: string }) =>
    (await api.post<{ data: { vendorAccountId: string; email: string; created: boolean; emailSent?: boolean } }>(`/api/v1/vendors/${vendorId}/invite`, input)).data.data,

  // ── Phase 1: customer-vendor relationships ────────────────────────────────
  listRelationships: list<VendorRelationship>('/api/v1/vendor-relationships'),
  createRelationship: async (input: { vendorId: string }) =>
    (await api.post<{ data: VendorRelationship }>('/api/v1/vendor-relationships', input)).data.data,
  updateRelationship: async (id: string, patch: Partial<VendorRelationship>) =>
    (await api.patch<{ data: VendorRelationship }>(`/api/v1/vendor-relationships/${id}`, patch)).data.data,

  // ── Phase 1: contract coverage rules ──────────────────────────────────────
  listCoverageRules: async (contractId: string) =>
    (await api.get<{ data: CoverageRule[] }>(`/api/v1/contracts/${contractId}/coverage-rules`)).data.data,
  addCoverageRule: async (contractId: string, input: {
    ruleType: string; operator?: string; targetId?: string | null; targetValue?: string | null; priority?: number;
  }) => (await api.post<{ data: CoverageRule }>(`/api/v1/contracts/${contractId}/coverage-rules`, input)).data.data,
  updateCoverageRule: async (contractId: string, ruleId: string, patch: Partial<CoverageRule>) =>
    (await api.patch<{ data: CoverageRule }>(`/api/v1/contracts/${contractId}/coverage-rules/${ruleId}`, patch)).data.data,
  removeCoverageRule: async (contractId: string, ruleId: string) =>
    (await api.delete(`/api/v1/contracts/${contractId}/coverage-rules/${ruleId}`)).data,

  // ── Phase 1: contract service teams ───────────────────────────────────────
  listServiceTeams: async (contractId: string) =>
    (await api.get<{ data: ContractServiceTeamRow[] }>(`/api/v1/contracts/${contractId}/service-teams`)).data.data,
  addServiceTeam: async (contractId: string, input: { name: string; vendorId: string; serviceMode?: string }) =>
    (await api.post<{ data: ContractServiceTeamRow }>(`/api/v1/contracts/${contractId}/service-teams`, input)).data.data,

  // ── Phase 1: contract technician eligibility (approved technicians) ───────
  listEligibleTechnicians: async (contractId: string) =>
    (await api.get<{ data: TechnicianEligibility[] }>(`/api/v1/contracts/${contractId}/eligible-technicians`)).data.data,
  addEligibleTechnician: async (contractId: string, input: { technicianId: string; eligibilityType?: string }) =>
    (await api.post<{ data: TechnicianEligibility }>(`/api/v1/contracts/${contractId}/eligible-technicians`, input)).data.data,
  removeEligibleTechnician: async (contractId: string, eligibilityId: string) =>
    (await api.delete(`/api/v1/contracts/${contractId}/eligible-technicians/${eligibilityId}`)).data,
  approveTechnician: async (contractId: string, technicianId: string) =>
    (await api.post(`/api/v1/contracts/${contractId}/technicians/${technicianId}/approve`, {})).data,
  revokeTechnician: async (contractId: string, technicianId: string) =>
    (await api.post(`/api/v1/contracts/${contractId}/technicians/${technicianId}/revoke`, {})).data,

  // ── Phase 1: ticket contract resolution + manual assignment ───────────────
  ticketResolution: async (ticketId: string) =>
    (await api.get<{ data: ContractResolutionRow | null }>(`/api/v1/tickets/${ticketId}/resolution`)).data.data,
  resolveContract: async (ticketId: string) =>
    (await api.post<{ data: { status: string; candidates: Array<{ contractId: string }> } }>(`/api/v1/tickets/${ticketId}/resolve-contract`, {})).data.data,
  selectContract: async (ticketId: string, contractId: string) =>
    (await api.post<{ data: TicketRow }>(`/api/v1/tickets/${ticketId}/select-contract`, { contractId })).data.data,
  assignTechnician: async (ticketId: string, input: { technicianId: string; assignmentRole?: string }) =>
    (await api.post<{ data: TicketRow }>(`/api/v1/tickets/${ticketId}/assign`, input)).data.data,
  unassignTicket: async (ticketId: string) =>
    (await api.post<{ data: TicketRow }>(`/api/v1/tickets/${ticketId}/unassign`, {})).data.data,
  ticketAssignmentHistory: async (ticketId: string) =>
    (await api.get<{ data: AssignmentHistoryRow[] }>(`/api/v1/tickets/${ticketId}/assignment-history`)).data.data,

  // ── Phase 5: customer contract dashboard ──────────────────────────────────
  contractDashboard: async () =>
    (await api.get<{ data: ContractDashboard }>('/api/v1/contract-dashboard')).data.data,
};

export interface ContractDashboard {
  contracts: { total: number; active: number; expiringSoon: number };
  vendors: number;
  tickets: { open: number; awaitingVendor: number; awaitingCustomer: number; breached: number; resolved: number; reopened: number };
  firstTimeFixRate: number | null;
  slaCompliance: number | null;
  byVendor: Array<{ vendorId: string; vendorName: string; open: number; breached: number }>;
}
